import { DATA_GRID_LAZY_PAGE_SIZE } from '@/components/DataGrid/definitions';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const useCardGridData = <T extends { id: string }>({
  fetchPage,
  resetKey,
  refreshKey,
  initialPageCount = 0,
  optimisticRemovedIds,
  onRefreshComplete,
}: {
  fetchPage: (parameters: { page: number; perPage: number }) => Promise<{ items: T[]; total: number }>;
  resetKey: string;
  refreshKey?: string;
  initialPageCount?: number;
  optimisticRemovedIds?: string[];
  onRefreshComplete?: () => void;
}) => {
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const currentPageReference = useRef(0);
  const generationReference = useRef(0);
  const isFetchingReference = useRef(false);
  const lastProcessedKeyReference = useRef<string | undefined>(undefined);
  const lastProcessedRefreshKeyReference = useRef<string | undefined>(refreshKey);
  const isRefreshingReference = useRef(false);
  const hasPendingRefreshReference = useRef(false);
  const hasPendingNextPageReference = useRef(false);

  const fetchPageReference = useRef(fetchPage);
  fetchPageReference.current = fetchPage;
  // Captured at mount and consumed by the first fetch: restoring a saved depth belongs to the
  // navigation that landed here. Re-reading it per render would re-apply it to every later reset —
  // a filter change would refetch as many pages as the last visit had scrolled through.
  const initialPageCountReference = useRef(initialPageCount);
  const onRefreshCompleteReference = useRef(onRefreshComplete);
  onRefreshCompleteReference.current = onRefreshComplete;
  const refreshLoadedPagesReference = useRef<() => Promise<void>>(() => Promise.resolve());

  const loadPages = async (pageCount: number) => {
    const results = await Promise.all(
      Array.from({ length: pageCount }, (_, index) =>
        fetchPageReference.current({ page: index + 1, perPage: DATA_GRID_LAZY_PAGE_SIZE })
      )
    );
    return { items: results.flatMap(r => r.items), total: results.at(-1)?.total ?? 0 };
  };

  const fetchNextPage = useCallback(async () => {
    if (isFetchingReference.current) {
      // A refresh is holding the slot. Remember the page the grid asked for so it isn't lost —
      // nothing else retries it while the user sits still at the bottom of the grid.
      hasPendingNextPageReference.current ||= isRefreshingReference.current;
      return;
    }
    isFetchingReference.current = true;

    const generation = generationReference.current;
    const nextPage = currentPageReference.current + 1;
    const isFirstPage = nextPage === 1;

    if (!isFirstPage) {
      setIsFetchingNextPage(true);
    }

    try {
      const pageCount = initialPageCountReference.current;
      initialPageCountReference.current = 0;
      if (isFirstPage && pageCount > 1) {
        const restored = await loadPages(pageCount);

        if (generationReference.current !== generation) return;

        currentPageReference.current = pageCount;
        setTotal(restored.total);
        setItems(restored.items);
      } else {
        const result = await fetchPageReference.current({ page: nextPage, perPage: DATA_GRID_LAZY_PAGE_SIZE });

        if (generationReference.current !== generation) return;

        currentPageReference.current = nextPage;
        setTotal(result.total);
        setItems(previous => (isFirstPage ? result.items : [...previous, ...result.items]));
      }
    } finally {
      if (generationReference.current === generation) {
        isFetchingReference.current = false;
        setIsLoading(false);
        setIsFetchingNextPage(false);
        if (hasPendingRefreshReference.current) {
          void refreshLoadedPagesReference.current();
        }
      }
    }
  }, []);

  const reset = () => {
    generationReference.current += 1;
    isFetchingReference.current = false;
    isRefreshingReference.current = false;
    // A new context supersedes any refresh or page still queued against the old one.
    hasPendingRefreshReference.current = false;
    hasPendingNextPageReference.current = false;
    currentPageReference.current = 0;
    setItems([]);
    setTotal(0);
    setIsLoading(true);
  };

  const refreshLoadedPages = useCallback(async () => {
    // A fetch already owns the slot; wait for it to hand over rather than dropping the refresh,
    // or the grid keeps showing pre-mutation data until the folder or search changes. This has to
    // come first: while page one is still loading there is no page count to refresh yet.
    if (isFetchingReference.current) {
      hasPendingRefreshReference.current = true;
      return;
    }

    const pageCount = currentPageReference.current;
    if (pageCount === 0) {
      hasPendingRefreshReference.current = false;
      return;
    }

    hasPendingRefreshReference.current = false;
    isFetchingReference.current = true;
    isRefreshingReference.current = true;
    setIsFetchingNextPage(true);

    const generation = generationReference.current;

    try {
      const refreshed = await loadPages(pageCount);

      if (generationReference.current !== generation) return;

      setTotal(refreshed.total);
      setItems(refreshed.items);
      onRefreshCompleteReference.current?.();
    } catch (error) {
      // Keep the pages already on screen; a failed refresh should not empty the grid.
      console.error('Failed to refresh the loaded card pages', error);
    } finally {
      if (generationReference.current === generation) {
        isFetchingReference.current = false;
        isRefreshingReference.current = false;
        setIsFetchingNextPage(false);
        if (hasPendingNextPageReference.current) {
          hasPendingNextPageReference.current = false;
          void fetchNextPage();
        }
      }
    }
  }, [fetchNextPage]);

  refreshLoadedPagesReference.current = refreshLoadedPages;

  const effectiveItems = useMemo(
    () => (optimisticRemovedIds?.length ? items.filter(item => !optimisticRemovedIds.includes(item.id)) : items),
    [items, optimisticRemovedIds]
  );
  // Only ids still present in the loaded pages count against the total. Once a refresh drops a
  // deleted asset from the server data, its id stops being subtracted and the total self-corrects.
  const effectiveTotal = Math.max(0, total - (items.length - effectiveItems.length));
  const hasNextPage =
    effectiveItems.length < effectiveTotal || (effectiveItems.length === 0 && currentPageReference.current === 0);

  useEffect(() => {
    if (lastProcessedKeyReference.current !== resetKey) {
      lastProcessedKeyReference.current = resetKey;
      reset();
      setIsFetchingNextPage(false);
    }

    if (currentPageReference.current === 0 && !isFetchingReference.current) {
      void fetchNextPage();
    }
  }, [resetKey]);

  useEffect(() => {
    if (lastProcessedRefreshKeyReference.current === refreshKey) return;
    lastProcessedRefreshKeyReference.current = refreshKey;
    void refreshLoadedPages();
  }, [refreshKey]);

  return {
    items: effectiveItems,
    total: effectiveTotal,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    reset,
    pageCount: currentPageReference.current,
  };
};
